/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  useCallback,
  useEffect,
  useState
} from 'react';
import {batch, useDispatch, useSelector} from 'react-redux';
import {useSystemOfUnits} from '@ion/applications/src/hooks/useSystemOfUnits';
import {errorOptions, successOptions} from '@ion/applications/src/prompts/constants';
import {translate} from '@ion/core/src/locale/locale';
import {Device} from '@ion/core/src/types/entities';
import {DeviceEditor} from '@ion/dls/src/editors/DeviceEditor';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';
import {useSwapDevice} from '@ion/query/src/hooks/useSwapDevice';
import {toggleUserPrompt} from '../../store/actions/user-prompt';
import {globalGroupSelector} from '../../store/selectors/global-group';
import {useAuthenticatedUser} from '../admin/users/useAuthenticatedUser';
import {refreshDeviceGrid, swapAdminDevice, VIEW_DEVICE} from './state';
import {useSwapDetails} from './useSwapDetails';
import {getDeviceIdentifier} from './ViewDeviceEditorUtils';

export interface SwapDeviceEditorProps {
  includeGroupProperties?: boolean;
  disableAccountChooser: boolean;
  includeSwapDevice?: boolean;
  showSCISettings?: boolean;
}

/**
 * Display Swap Device Editor
 */
export const SwapDeviceEditor = ({
  showSCISettings = false,
  includeSwapDevice = false,
  disableAccountChooser = false,
  includeGroupProperties = true
}: SwapDeviceEditorProps) => {
  const dispatch                  = useDispatch();
  const globalGroup               = useSelector(globalGroupSelector);
  const [clearForm, setClearForm] = useState<boolean>(false);
  const {account}                 = useAuthenticatedUser();
  const systemOfUnits             = useSystemOfUnits();
  const {
    showConfirmSwapDevice,
    showSwapDevice,
    selectedSwapDevice,
    swapColumns,
    isAssetAssociated,
    isGroupAssociated,
    isSCIDevice,
    swapAssociatedTitle,
    swapAssociatedMessage,
    getDismissLabel,
    onSwapAction,
    onSwapDismiss
  } = useSwapDetails();

  const {
    save,
    isSuccess,
    isError,
    isLoading
  } = useSwapDevice(selectedSwapDevice?.id);

  useEffect(() => {
    if (isSuccess) {
      setClearForm(true);
      batch(() => {
        dispatch(swapAdminDevice(null, false));
        dispatch({type: VIEW_DEVICE, device: null, show: false});
        dispatch(toggleUserPrompt({
          ...successOptions,
          message : translate('admin-devices:swap.swapSuccess',
            {entity: translate('common:entityLabels.device')})
        }));
      });
      dispatch(refreshDeviceGrid());
    }
    if (isError) {
      dispatch(toggleUserPrompt({
        ...errorOptions,
        message : translate('admin-devices:swap.swapError',
          {entity: translate('common:entityLabels.device')})
      }));
    }
  }, [isSuccess, isError]);

  const onDeviceSave = useCallback((device: Device) => {
    const identifier = getDeviceIdentifier(selectedSwapDevice);
    const deviceObj = {};
    swapColumns.forEach(key => {
      deviceObj[key] = device[key];
    });
    save({identifier, device: deviceObj});
  }, [selectedSwapDevice]);

  const onCancel = useCallback(() => {
    dispatch(swapAdminDevice(null, false));
  }, []);

  return (
    <>
      {showSwapDevice && (
        <DeviceEditor open
          includeAdditionalDeviceFields
          includeDatetimeInformation
          disableAccountChooser={disableAccountChooser}
          loading={isLoading}
          account={globalGroup || account}
          device={selectedSwapDevice}
          systemOfUnits={systemOfUnits}
          includeGroupProperties={includeGroupProperties}
          includeSwapDevice={includeSwapDevice}
          showSCISettings={showSCISettings}
          isViewDialog={false}
          clear={clearForm}
          onSubmit={onDeviceSave}
          onCloseDialog={onCancel} />
      )}
      {showConfirmSwapDevice && (
        <UserNotificationDialog onBackdropClick={onSwapDismiss}
          onDismiss={onSwapDismiss}
          onAction={(isGroupAssociated && (!isAssetAssociated || isSCIDevice)) ? onSwapAction : undefined}
          title={swapAssociatedTitle()?.toUpperCase()}
          message={swapAssociatedMessage()}
          actionLabel={translate('common:groupChooser.yesContinue')}
          dismissLabel={getDismissLabel()}
          show />
      )}
    </>
  );
};

export default memo(SwapDeviceEditor);
